import { useCallback, useMemo, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DialogProps, Heading, Text } from 'react-aria-components';
import {
  CourseDialogBody,
  CourseDialogFooter,
} from 'src/components/ui/CourseInfoDialog';
import Button from 'src/components/ui/Button';
import FormTextField from 'src/components/form/FormTextField';
import BlockTitle from 'src/components/ui/BlockTitle';
import Dialog from 'src/components/ui/Dialog';
import Switch from 'src/components/ui/Switch';
import FormMultiSelect from 'src/components/form/FormMultiSelect';
import CheckIcon from 'src/assets/icons/shield-check.svg';
import { getGraphqlError } from 'src/utils';
import CONTACT_MUTATION from 'src/graphql/mutation/contact.graphql';
import INSTITUTION_LIST from 'src/graphql/query/institution-list.graphql';

import type { Institution } from 'src/types/institution';
import type { Size } from 'src/types/common';

interface FeedbackDialogProps extends Omit<DialogProps, 'children'> {
  className?: string;
  size?: Size;
  onClose?: () => void;
}

const FeedbackDialog = ({
  size = 'medium',
  className,
  onClose,
  ...props
}: FeedbackDialogProps) => {
  const isSmall = size === 'small';
  const [success, setSuccess] = useState(false);

  const [sendMessage, { loading, error }] = useMutation(CONTACT_MUTATION, {
    onCompleted: () => {
      setSuccess(true);
    },
  });

  const { handleSubmit, clearErrors, control, watch, ...methods } =
    useForm<FeedbackSchemaType>({
      resolver: zodResolver(FeedbackSchema),
      defaultValues: {
        firstName: '',
        lastName: '',
        email: '',
        institutions: [],
        bookstore: '',
        institutionName: '',
        organization: '',
        organizationMember: false,
      },
    });

  const isOrganizationMember = watch('organizationMember');

  const onSubmit = useCallback(
    async ({
      firstName,
      lastName,
      email,
      organization,
      institutions,
      institutionName,
      bookstore,
      organizationMember,
    }: FeedbackSchemaType) => {
      clearErrors();

      const institutionText = organizationMember
        ? `Organization: ${organization}\nInstitution: ${institutionName || 'Not Specified'}\nBookstore: ${bookstore || 'Not Specified'}`
        : `Institutions: ${institutions.map((i) => i.label).join(', ')}`;

      const message = `My name is ${firstName} ${lastName}, and I am reaching out to request access. Below are my details:\n\nEmail: ${email}\n${institutionText}`;

      await sendMessage({
        variables: { subject: 'Request for Access', message, email },
      });
    },
    [sendMessage, clearErrors]
  );

  return (
    <Dialog className={twMerge('p-0 overflow-hidden', className)} {...props}>
      {({ close }) => {
        if (success)
          return (
            <SuccessScreen
              onClose={() => {
                close();
                if (onClose) onClose();
              }}
            />
          );

        return (
          <FormProvider
            watch={watch}
            control={control}
            clearErrors={clearErrors}
            handleSubmit={handleSubmit}
            {...methods}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex-1 flex flex-col gap-6 justify-end min-h-0 overflow-auto">
                <CourseDialogBody className="text-surface-90">
                  <Heading slot="title" className="text-2xl font-bold">
                    Request Access
                  </Heading>

                  <BlockTitle variant="v2">Creator Information</BlockTitle>

                  <div className="grid grid-cols-2 gap-x-2.5 gap-y-4">
                    <FormTextField
                      name="firstName"
                      label="First Name"
                      // The user opened this dialog to request access; start at the first required field.
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      isRequired
                      autoComplete="given-name"
                    />
                    <FormTextField
                      name="lastName"
                      label="Last Name"
                      isRequired
                      autoComplete="family-name"
                    />
                    <FormTextField
                      name="email"
                      label="Email"
                      isRequired
                      autoComplete="email"
                    />

                    <Controller
                      name="organizationMember"
                      control={control}
                      render={({ field: { value, ...rest } }) => (
                        <Switch
                          defaultSelected={value}
                          className="text-sm leading-4 text-right mb-2 justify-self-end self-end"
                          showText
                          {...rest}
                        >
                          Are you an organization member?
                        </Switch>
                      )}
                    />

                    {isOrganizationMember ? (
                      <>
                        <FormTextField
                          name="organization"
                          label="Organization"
                          isRequired
                        />
                        <FormTextField
                          name="institutionName"
                          label="Institution"
                        />
                        <FormTextField name="bookstore" label="Bookstore" />
                      </>
                    ) : (
                      <InstitutionsMultiSelect />
                    )}
                  </div>
                </CourseDialogBody>
              </div>

              <CourseDialogFooter isSmall={isSmall}>
                {error && <ErrorText error={error} />}
                <Button
                  className="justify-center"
                  variant="surface"
                  onPress={close}
                  ghost
                >
                  Cancel
                </Button>
                <Button
                  className="justify-center"
                  type="submit"
                  variant="success"
                  isDisabled={loading}
                  loading={loading}
                >
                  Create
                </Button>
              </CourseDialogFooter>
            </form>
          </FormProvider>
        );
      }}
    </Dialog>
  );
};

const InstitutionsMultiSelect = () => {
  const { data, loading } = useQuery(INSTITUTION_LIST);
  const institutions = useMemo(() => {
    const list = data?.institutionsList ?? [];

    return list
      .map((institution: Institution) => ({
        value: institution.institutionId,
        label: institution.name,
      }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [data]);

  return (
    <FormMultiSelect
      name="institutions"
      label="Institution(s)"
      placeholder="Select an institution..."
      options={institutions}
      isRequired
      isDisabled={loading}
      className="col-span-full"
      maxMenuHeight={180}
    />
  );
};

const SuccessScreen = ({ onClose }: { onClose: () => void }) => (
  <div className="px-4 pb-4 text-center text-base font-medium text-surface-alt-90">
    <CheckIcon className="inline-block w-20 h-20" />
    <Heading level={5} className="text-2xl font-bold mt-9 mb-4">
      Message Sent!
    </Heading>
    <Text elementType="p">
      It has been successfully submitted and is currently being processed. Your
      account manager will be in touch soon.
    </Text>

    <Button className="mt-4" onPress={onClose} variant="secondary">
      Close
    </Button>
  </div>
);

const ErrorText = ({ error }: { error: ApolloError }) => {
  const { message, general } = getGraphqlError(error);

  return (
    <Text role="alert" className="text-xs text-red font-medium">
      {message || general}
    </Text>
  );
};

const FeedbackSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email(),
    organizationMember: z.boolean(),
    organization: z.string(),
    bookstore: z.string(),
    institutionName: z.string(),
    institutions: z.array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    ),
  })
  .superRefine(({ institutions, organization, organizationMember }, ctx) => {
    if (organizationMember && organization.length <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Organization is required',
        path: ['organization'],
      });
    }

    if (!organizationMember && institutions.length <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please add at least one institution',
        path: ['institutions'],
      });
    }

    return ctx;
  });

type FeedbackSchemaType = z.infer<typeof FeedbackSchema>;

export default FeedbackDialog;
